
import gql from 'graphql-tag';
import { mapGetters } from 'vuex';
import { EventBus } from '@/eventBus';
import { defineComponent } from 'vue';
import ViewsRecipient_customTags from './custom-tags.graphql';
import ViewsRecipient_userInterfaceDetails from './user-interface-details.graphql';
import TranslationHelper from '@/helpers/translation';
import { recipientAddTag, recipientRemoveTag, recipientEndCollection } from '@/api/recipient';
import Logging from '@/logging';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import MessagesTable from '@/views/_messages/components/MessagesTable.vue';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';

export default defineComponent({
	apollo: {
		customTags: {
			query: ViewsRecipient_customTags,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			}
		},
		userInterfaceDetails: {
			query: ViewsRecipient_userInterfaceDetails,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0],
			skip() {
				return !this.recipient;
			}
		}
	},
	components: {
		MessagesTable
	},
	props: {
		recipientRef: { type: String, required: true }
	},
	data() {
		return {
			activeTab: 'MESSAGES',
			recipient: null,
			rightPanelLayoutVariables: {
				mainPanelWidth: 17,
				rightPanelWidth: 7
			},
			showTagSelect: false,
			customTags: []
		};
	},
	computed: {
		...mapGetters(['env', 'beta', 'selectedParticipantStore', 'availableParticipants', 'recipientRightPanelCollapsed', 'loggedInUser']),
		hasMessagesPermission(): boolean {
			const licences = this.$store.state.risolto.licences[this.selectedParticipantStore];
			const permissions = this.$store.state.risolto.permissions[this.selectedParticipantStore];

			return hasLicenseAndPermission(
				licences,
				permissions,
				'MAILBOX'
			);
		}
	},
	watch: {
		'$route.name': {
			immediate: true,
			handler(newName: string) {
				if (newName === 'recipient-detail') {
					this.activeTab = 'DETAIL';
				} else if (newName === 'recipient-messages') {
					this.activeTab = 'MESSAGES';
				} else if (newName === 'recipient-archive') {
					this.activeTab = 'ARCHIVE';
				}
			}
		},
		selectedParticipantStore() {
			this.getRecipient();
		},
		recipientRightPanelCollapsed() {
			this.evaluateLayout();
		},
		'$store.state.apollo.risolto_message': function() {
			this.getRecipient();
		}
	},
	created() {
		this.getRecipient();

		this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'recipient');
		this.$store.dispatch('risolto/setSelectedInvoices', []);

		this.$store.commit('messages/SET_MESSAGE_TYPE', 'IN');
		this.$store.commit('messages/SET_FILTER_LINKED', [true, false]);
		this.$store.commit('messages/SET_FILTER_RECIPIENT', this.recipientRef);

		this.evaluateLayout();
	},
	destroyed() {
		this.$store.commit('risolto/SET_SELECTED_RECIPIENT', undefined);
		this.$store.commit('risolto/SET_SHOW_ACTION_TABLE_FILTERS', false);
	},
	methods: {
		handleTabClick(tab: { name: string }): void {
			const { id } = this.$route.params;

			if (tab.name === 'DETAIL') {
				this.$router.push({ name: 'recipient-detail', params: { id } });
			} else if (tab.name === 'MESSAGES') {
				this.$router.push({ name: 'recipient-messages', params: { id } });
			} else if (tab.name === 'ARCHIVE') {
				this.$router.push({ name: 'recipient-archive', params: { id } });
			}
		},
		evaluateLayout(): void {
			if (this.recipientRightPanelCollapsed) {
				this.rightPanelLayoutVariables.mainPanelWidth = 24;
				this.rightPanelLayoutVariables.rightPanelWidth = 0;
			} else {
				this.rightPanelLayoutVariables.mainPanelWidth = 17;
				this.rightPanelLayoutVariables.rightPanelWidth = 7;
			}
		},
		toggleRightPanel(value: boolean): void {
			this.$store.commit('recipient/SET_RIGHT_PANEL_COLLAPSED', value);
		},
		getRecipient(): void {
			if (this.selectedParticipantStore !== null) {
				const vars = {
					recipient: this.$route.params.id,
					participant: this.selectedParticipantStore
				};

				var query = this.makeQuery();
				Logging.log('RecipientDetail.getRecipient: query - : ', query);

				this.$apollo
					.query({
						query: query,
						variables: vars
					})
					.then(result => {
						Logging.log('RecipientDetail.getRecipient: data - ', result.data.risolto_recipient[0]);
						this.recipient = result.data.risolto_recipient[0];
						Logging.log('RecipientDetail.getRecipient - recipient: ', result.data.risolto_recipient[0]);
						this.$store.commit('risolto/SET_SELECTED_RECIPIENT', this.recipient);
					})
					.catch(e => {
						Logging.error('Error in recp-detail/created/GET_RECIPIENT: ', e);
					});
			}
		},
		customFormatter(value: string): string {
			return format(new Date(value), 'eeee, d MMMM yyyy - HH:mm', { locale: enGB });
		},
		checkParticipants(): void {
			Logging.log('this.availableParticipants: ', this.availableParticipants);
			try {
				if (this.availableParticipants.length >= 1) {
					const routeParticipant = this.$route.params.participant;

					if (routeParticipant !== this.selectedParticipantStore) {
						const i = this.availableParticipants.findIndex(el => {
							return el.reference === routeParticipant;
						});

						if (i === -1) {
							this.$notify({
								title: this.$t('warning'),
								message: this.$t('notAllowed'),
								type: 'error'
							});
							this.$router.push({
								name: 'dashboard'
							});
						} else {
							EventBus.$emit('setParticipant', routeParticipant);
						}
					}
				}
			} catch (e) {
				Logging.error('Smth went wrong in recpDetail/checkParticipants()', e);
			}
		},
		makeQuery() {
			var query = gql`
				query risolto_recipient($participant: uuid!, $recipient: uuid!) {
					risolto_recipient(where: { reference: { _eq: $recipient }, participant: { _eq: $participant } }) {
						last_dunning_costs
						in_collection
						last_dunning_run_at
						name
						account_id
						participant
						participant_by_reference {
							reference
						}
						legal_entity
						legal_entity_by_reference {
							reference
							registration_name
							company_id
							company_id_type
						}
						address
						contacts {
							reference
							first_name
							last_name
						}
						channel_endpoints (where: { deactivated_at: { _is_null: true } }) {
							channel
							contact_by_reference {
								first_name
								last_name
							}
						}
						due_balance_amount
						balance_amount
						reference
						custom_fields
						legal_id_type
						legal_id
						type
						dunning_is_dirty
						prediction_stars
						tags {
							tag
							tag_name
							can_remove
						}
						collections {
							custom_fields
						}
						message_recipients(
							where: { message_by_reference: { archived_at: { _is_null: true } } }
						) {
							reference
						}
					}
				}
			`;
			return query;
		},
		translateTag(tag: string): string {
			return TranslationHelper.tag(this, tag);
		},
		handleAddTagClick(): void {
			this.showTagSelect = true;
			this.$nextTick(_ => {
				this.$refs.selectTag.focus();
			});
		},
		async handleAddTagAdd(value: string): Promise<void> {
			if (!this.recipient) return;

			await recipientAddTag(
				this.selectedParticipantStore,
				this.recipient.legal_entity,
				this.recipient.reference,
				value
			);
			this.getRecipient();

			this.showTagSelect = false;
		},
		/**
		 * @param value tag reference
		 * @param name tag name
		 */
		async handleTagRemove(value: string, name: string): Promise<void> {
			if (!this.recipient) return;

			if (name === 'COLLECTION') {
				await this.$confirm(this.$i18n.t('RecipientDetail.confirm_end_collection_message'), 'Warning', {
					confirmButtonText: this.$i18n.t('RecipientDetail.confirm_end_collection_confirm'),
					cancelButtonText: this.$i18n.t('RecipientDetail.confirm_end_collection_cancel'),
					type: 'warning'
				}).then(async () => {
					this.$message({
						type: 'success',
						message: this.$i18n.t('RecipientDetail.confirm_end_collection_succeeded')
					});
					try {
						await recipientEndCollection(
							this.selectedParticipantStore,
							this.recipient.legal_entity,
							this.recipient.reference,
							this.loggedInUser.username,
							this.loggedInUser.reference
						);
					} catch (e) {
						Logging.error(e);
						this.$message({
							type: 'error',
							message: this.$i18n.t('RecipientDetail.confirm_end_collection_failure')
						});
					}
				}).catch(() => {
					this.$message({
						type: 'info',
						message: this.$i18n.t('RecipientDetail.confirm_end_collection_canceled')
					});
				});
			} else {
				await recipientRemoveTag(
					this.selectedParticipantStore,
					this.recipient.legal_entity,
					this.recipient.reference,
					value
				);
			}
			this.getRecipient();
		}
	}
});
